import { Anchor, Button, Flex, Text } from '@mantine/core'
import { useRouter } from 'next/router'
import { useMemo, useState } from 'react'

import { useAnalytics } from '@flecto/analytics/src'
import { type FlectoAnalyticsEvents } from '@flecto/analytics/src/events'
import { type GetAllInsurancesOptions } from '@flecto/api/saas'
import { useQueryGetAllInsurances } from '@flecto/core/saas'
import { useQueryGetUserSubscription } from '@flecto/core/shared'
import { Trans, useTranslation } from '@flecto/translations'
import { type UNSAFE_any, type CoreInsurance } from '@flecto/types/src'
import { Alert, Filters, type DataTableSortStatus } from '@flecto/ui/@shared'
import { type FilterValue } from '@flecto/ui/@shared/Filters/components/multi-select/filterTypeConfig'
import { Page } from '@flecto/ui/saas'
import { useIntercom } from '@flecto/utils/src'

import {
  TableInsuranceList,
  useInsuranceFilterFields,
  useProcessInsuranceFilters,
} from '..'
import { FiltersInsurances } from '../components/FiltersInsurances/FiltersInsurances'

export function PageInsuranceList() {
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const { showIntercomWithMessage } = useIntercom()
  const { processInsuranceFilters } = useProcessInsuranceFilters()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filtersCustom, setFiltersCustom] = useState({})
  const [sortBy, setSortBy] = useState<GetAllInsurancesOptions['sort']>('id')
  const [orderBy, setOrderBy] =
    useState<GetAllInsurancesOptions['order']>('desc')

  const insuranceFilterFields = useInsuranceFilterFields()
  const track = useAnalytics('segment')
  const router = useRouter()
  const pageSize = 10

  const { data: subscription, isLoading: isLoadingSubscription } =
    useQueryGetUserSubscription()

  const { data, isLoading } = useQueryGetAllInsurances({
    q: searchTerm,
    limit: pageSize,
    offset: currentPage * pageSize - pageSize,
    sort: sortBy,
    order: orderBy,
    ...filtersCustom,
  })

  const insurancesData = useMemo(() => data?.insurance ?? [], [data?.insurance])
  const totalRecords = useMemo(
    () => data?.total_count ?? 0,
    [data?.total_count]
  )

  const handleFilterChanges = (filtersApplied: FilterValue[]) => {
    const filters = filtersApplied
      ? processInsuranceFilters(filtersApplied)
      : {}
    setFiltersCustom(filters)
    setCurrentPage(1)
  }

  const handleTrackEvent = (
    eventName: keyof FlectoAnalyticsEvents,
    data: Record<string, UNSAFE_any>
  ) => {
    track(eventName, data)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const handleSearch = (term: string) => {
    setCurrentPage(1)
    setSearchTerm(term)
  }

  const handleSortChanges = (sort: DataTableSortStatus<CoreInsurance>) => {
    setSortBy(sort.columnAccessor as GetAllInsurancesOptions['sort'])
    setOrderBy(sort.direction)
  }

  return (
    <Page>
      <Page.Title>
        <Flex justify='space-between'>
          {t('insurances_page_title')}
          {subscription && subscription?.plan !== 'Starter' && (
            <Button
              onClick={() => {
                router.push('/insurances/create')
                track('INSURANCE_FORM__NEW', {})
              }}
            >
              {t('create_new_insurance_button')}
            </Button>
          )}
        </Flex>
      </Page.Title>
      <Page.Content>
        {subscription?.plan === 'Starter' && (
          <Alert
            data-testid='insurance-subscription-alert'
            mb='md'
            variant='info'
            description={
              <Text c='blue.7'>
                <Trans
                  ns='saas'
                  i18nKey='insurances.insurance_alert_subscription'
                  values={{ plan: subscription?.plan }}
                  components={[
                    <strong key={0} />,
                    <Anchor
                      key={1}
                      component='button'
                      onClick={() => showIntercomWithMessage('')}
                      c='blue.7'
                      fw={500}
                      ml='xs'
                      style={{ textDecoration: 'underline' }}
                    >
                      {t('insurance_alert_button')}
                    </Anchor>,
                  ]}
                />
              </Text>
            }
          />
        )}

        <Flex direction='column' gap='md'>
          <Flex justify='flex-end' gap='md'>
            <Filters
              id='insurances-filters'
              isLoading={isLoading}
              fields={insuranceFilterFields}
              onFilter={handleFilterChanges}
              onTrackEvent={handleTrackEvent}
            />
            <FiltersInsurances onFilter={handleSearch} />
          </Flex>

          <TableInsuranceList
            data-testid='insurances-table'
            insurances={insurancesData}
            isLoading={isLoading || isLoadingSubscription}
            onSortStatusChange={handleSortChanges}
            sortStatus={{
              columnAccessor: sortBy ?? '',
              direction: orderBy ?? 'desc',
            }}
            withPaginationControlled={{
              currentPage,
              totalRecords,
              onPageChange: handlePageChange,
            }}
          />
        </Flex>
      </Page.Content>
    </Page>
  )
}
